import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, XCircle } from "lucide-react";
import { euro } from "../lib/format";

export const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

export const weekDays = (monday) =>
  Array.from({ length: 6 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

const DAY = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

export const AdminStats = ({ bookings }) => {
  const today = toISO(new Date());
  const in7 = toISO(new Date(Date.now() + 7 * 86400000));
  const active = bookings.filter((b) => b.status !== "cancelled");
  const todayList = active.filter((b) => b.date === today);
  const next7 = active.filter((b) => b.date > today && b.date <= in7);
  const cards = [
    { id: "today-count", label: "Oggi", value: todayList.length, sub: "appuntamenti" },
    { id: "today-total", label: "Incasso previsto oggi", value: `€${euro(todayList.reduce((s, b) => s + b.total, 0))}`, sub: `${todayList.reduce((s, b) => s + (b.duration || (b.slots_count || 1) * 20), 0)} min di lavoro` },
    { id: "next7-count", label: "Prossimi 7 giorni", value: next7.length, sub: `€${euro(next7.reduce((s, b) => s + b.total, 0))} previsti` },
    { id: "next-booking", label: "Prossimo cliente", value: todayList[0] ? todayList[0].time : "—", sub: todayList[0] ? todayList[0].name : "nessuno oggi" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.id} data-testid={`admin-stat-${c.id}`} className="rounded-2xl border border-[rgba(11,31,58,0.15)] bg-white px-5 py-4">
          <p className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#6F6466]">{c.label}</p>
          <p className="font-display text-3xl font-bold text-[#0B1F3A] mt-1 leading-none">{c.value}</p>
          <p className="text-xs text-[#6F6466] mt-1 truncate">{c.sub}</p>
        </div>
      ))}
    </div>
  );
};

export const WeekView = ({ bookings, onCancel, busyId, monday, setMonday }) => {
  const days = useMemo(() => weekDays(monday), [monday]);
  const today = toISO(new Date());
  const shift = (n) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + n * 7);
    setMonday(d);
  };
  const byDate = bookings.reduce((acc, b) => ((acc[b.date] = acc[b.date] || []).push(b), acc), {});

  return (
    <div data-testid="admin-week-view">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="admin-week-prev" onClick={() => shift(-1)} className="rounded-full border border-[rgba(11,31,58,0.2)] p-2 hover:bg-[#0B1F3A] hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <p className="font-display font-semibold text-lg">
          {days[0].toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – {days[5].toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
        </p>
        <div className="flex gap-2">
          <button data-testid="admin-week-today" onClick={() => setMonday(mondayOf(new Date()))} className="rounded-full border border-[rgba(11,31,58,0.2)] px-3 text-xs uppercase tracking-wider hover:bg-[#0B1F3A] hover:text-white transition-colors">Oggi</button>
          <button data-testid="admin-week-next" onClick={() => shift(1)} className="rounded-full border border-[rgba(11,31,58,0.2)] p-2 hover:bg-[#0B1F3A] hover:text-white transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {days.map((d) => {
          const iso = toISO(d);
          const list = (byDate[iso] || []).sort((a, b) => a.time.localeCompare(b.time));
          const active = list.filter((b) => b.status !== "cancelled");
          return (
            <div key={iso} data-testid={`admin-week-day-${iso}`} className={`rounded-2xl border bg-white min-h-[220px] flex flex-col ${iso === today ? "border-[#0B1F3A]" : "border-[rgba(11,31,58,0.15)]"}`}>
              <div className={`px-3 py-2 rounded-t-2xl flex items-baseline justify-between ${iso === today ? "bg-[#0B1F3A] text-white" : "bg-[#F7F2EC]"}`}>
                <p className="text-xs uppercase tracking-wider">{DAY[d.getDay()]} <span className="font-display font-bold text-lg ml-1">{d.getDate()}</span></p>
                <p className="text-[11px] font-mono-alt opacity-80">{active.length} · €{euro(active.reduce((s, b) => s + b.total, 0))}</p>
              </div>
              <div className="p-2 space-y-1.5 flex-1">
                {list.length === 0 && <p className="text-xs text-[#9A8F90] px-1 pt-2">Libero</p>}
                {list.map((b) => (
                  <div key={b.id} data-testid={`admin-week-booking-${b.id}`} className={`group rounded-lg border border-[rgba(11,31,58,0.1)] px-2 py-1.5 text-xs ${b.status === "cancelled" ? "opacity-40 line-through" : ""}`}>
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-mono-alt font-semibold text-[#0B1F3A]">{b.time}</span>
                      {b.status !== "cancelled" && (
                        <button data-testid={`admin-week-cancel-${b.id}`} onClick={() => onCancel(b)} disabled={busyId === b.id} aria-label="Annulla" className="opacity-0 group-hover:opacity-100 text-[#6F6466] hover:text-[#0B1F3A] transition-opacity">
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="font-semibold truncate">{b.name}</p>
                    <p className="text-[#6F6466] truncate">{b.service_names.join(" + ")} · €{euro(b.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
